import React from "react";
import { ArchMap, LINK_COLORS } from "../archmap/ArchMap";
import { MapCard, MapNode } from "../archmap/types";
import { externals, links, openIssues, publicHosts, retired, Server, ServerStatus, servers } from "./servers";

const STATUS: Record<ServerStatus, { label: string; color: string }> = {
  migrated: { label: "Migrated", color: "#43a047" },
  kept: { label: "Stays", color: "#005f98" },
  partial: { label: "Wiki only", color: "#8d6e63" },
  pending: { label: "Migration planned", color: "#e5ac58" },
  retired: { label: "Retired", color: "#9e9e9e" },
};

// Column layout: public hosts, then the servers in three columns; legacy servers and external systems below
const COLUMNS = [["hosts"], ["s193", "s3"], ["s8", "s201"], ["s7", "s6"]];
const BOTTOM = ["s206", "s1", "s2", "externals", "retired"];

const serverCard = (server: Server): MapCard => ({
  id: server.id,
  title: server.dns,
  subtitle: server.ip,
  description: server.role,
  badge: STATUS[server.status],
  chips: [...(server.user ? [`user ${server.user}`] : []), server.os],
  footer: [server.replaces && `Replaces ${server.replaces.join(", ")}`, server.note].filter(Boolean).join(" · ") || undefined,
  accent: STATUS[server.status].color,
  details: [
    ["User", server.user ?? "–"],
    ["OS", server.os],
    ["Status", STATUS[server.status].label],
    ["Services", String(server.services.length)],
    ...(server.replaces ? [["Replaces", server.replaces.join(", ")] as [string, string]] : []),
  ],
  note: server.note ?? server.role,
  nodes: server.services.map((svc): MapNode => {
    const port = svc.port?.split(" ")[0];
    return {
      id: svc.id,
      name: svc.name,
      tag: svc.port,
      kind: svc.kind,
      group: svc.group,
      state: svc.state,
      note: svc.note,
      url: port && /^\d+$/.test(port) ? `http://${server.ip}:${port}` : undefined,
      details: [
        ...(svc.containerPort ? [["Container", svc.containerPort] as [string, string]] : []),
        ...(svc.image ? [["Image", svc.image] as [string, string]] : []),
        ["Group", svc.group],
      ],
    };
  }),
});

const cards: MapCard[] = [
  {
    id: "hosts",
    title: "Public hosts",
    description: "*.genesiscloud.cc unless noted · DNS → 192.168.66.193 · Nginx Proxy Manager",
    accent: LINK_COLORS.proxy,
    nodes: publicHosts.map((h) => ({
      id: h.id,
      name: h.host.replace(".genesiscloud.cc", ""),
      kind: "proxy",
      mono: true,
      state: h.warn ? "warn" : undefined,
      note: h.warn,
      url: `https://${h.host}`,
      details: [
        ["Host", h.host],
        ["Forward", h.forward],
      ],
    })),
  },
  ...servers.map(serverCard),
  {
    id: "externals",
    title: "External systems",
    description: "Called by the services above, not running on these servers",
    accent: "#757575",
    nodes: externals.map((e) => ({ id: e.id, name: e.name, tag: e.tag, kind: "external", group: e.group, note: e.note })),
  },
  {
    id: "retired",
    title: "Retired VMs",
    description: "Old 192.168.66.x VMs, services moved to the new network",
    accent: STATUS.retired.color,
    nodes: retired.map((r) => ({
      id: r.id,
      name: `${r.ip.replace("192.168.", "")} ${r.dns}`,
      tag: `→ ${servers.find((s) => s.id === r.movedTo)?.dns}`,
      kind: "external",
      state: "stopped",
      note: r.was,
      related: [r.movedTo],
      details: [
        ["Was", r.was],
        ["Moved to", servers.find((s) => s.id === r.movedTo)?.dns ?? r.movedTo],
      ],
    })),
  },
];

export const OnPremMap: React.FunctionComponent = () => (
  <ArchMap
    title="Digitalisierung On-Premises Servers"
    subtitle="New network 10.192.193.0/24 · all public hosts go through Nginx Proxy Manager on dev-rpx · inventory of 2026-11-03"
    stats={[
      { label: "Migrated", value: servers.filter((s) => s.status === "migrated").length },
      { label: "Services", value: servers.reduce((n, s) => n + s.services.length, 0) },
      { label: "Public hosts", value: publicHosts.length },
      { label: "Retired VMs", value: retired.length },
    ]}
    cards={cards}
    columns={COLUMNS}
    gridTemplate="minmax(180px, 0.75fr) repeat(3, minmax(230px, 1fr))"
    bottom={BOTTOM}
    links={links}
    legend={(Object.keys(STATUS) as ServerStatus[]).map((s) => STATUS[s])}
    issues={openIssues}
  />
);
